import { ApolloProvider } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { Integrations } from "@sentry/tracing";
import "core-js/stable";
import flatten from "flat";
import { merge } from "lodash";
import React, { useMemo } from "react";
import { IntlProvider } from "react-intl";
import "regenerator-runtime/runtime";
import apolloClient from "./apolloClient";
import ViewerProvider from "./components/ViewerProvider";
import { WombatThemeProvider } from "./components/wombat-ui";
import useViewer from "./hooks/useViewer";
import DefaultEnglish from "./locales/en-US.json";
import DefaultFrench from "./locales/fr-FR.json";
import AppRouter from "./routers/AppRouter";

/* istanbul ignore if  */
if (process.env.NODE_ENV !== "development") {
  Sentry.init({
    dsn:
      "https://496a65ee94864cd88fd099b1a275a98a@o443284.ingest.sentry.io/5416778",
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
  });
}

const LocalizedApp = () => {
  const user = useViewer();

  const locale = user ? user.locale : navigator.language || "fr-FR";
  const language = locale.split("-")[0];
  const localeNamespace = user?.company?.localeNamespace;

  const translationsByLanguages = useMemo(() => {
    if (localeNamespace) {
      return [
        {
          language: "en",
          translations: merge(
            DefaultEnglish,
            //eslint-disable-next-line
            require(`./locales/${localeNamespace}/en-US.json`)
          ),
        },
        {
          language: "fr",
          translations: merge(
            DefaultFrench,
            //eslint-disable-next-line
            require(`./locales/${localeNamespace}/fr-FR.json`)
          ),
        },
      ];
    } else {
      return [
        {
          language: "en",
          translations: DefaultEnglish,
        },
        {
          language: "fr",
          translations: DefaultFrench,
        },
      ];
    }
  }, [localeNamespace]);

  const translations = useMemo(
    () =>
      flatten(
        translationsByLanguages.find((t) => t.language === language)
          ?.translations || DefaultFrench,
        { safe: true }
      ),
    [translationsByLanguages, language]
  );

  return (
    <IntlProvider locale={locale} messages={translations}>
      <AppRouter />
    </IntlProvider>
  );
};

const App = () => {
  return (
    <ApolloProvider client={apolloClient}>
      <ViewerProvider>
        <WombatThemeProvider>
          <LocalizedApp />
        </WombatThemeProvider>
      </ViewerProvider>
    </ApolloProvider>
  );
};

export default App;
